<div>
  <p class="text-sm text-gray-500 mb-4">
    ClearScrap revenue per customer in the year after their first pickup, for customers whose first pickup was in the selected
    dates. Choose a range of up to four quarters to compare cohorts.
  </p>

  <div *ngIf="errorMessage" class="bg-red-50 text-red-700 border border-red-200 rounded-md px-4 py-3 mb-4 text-sm">
    <i class="fas fa-exclamation-circle mr-1"></i> {{ errorMessage }}
  </div>

  <div *ngIf="loading" class="text-center text-gray-500 py-10">
    <i class="fas fa-spinner fa-spin mr-1"></i> Loading LTV...
  </div>

  <div *ngIf="!loading && report">
    <!-- Headline tiles -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="text-xs font-semibold text-gray-500 uppercase mb-1">12-month LTV</div>
        <div class="text-2xl font-bold text-green-700">{{ report.totals.twelveMonthLtv != null ? (report.totals.twelveMonthLtv | currency:'INR':'symbol':'1.0-0') : '—' }}</div>
        <div class="text-xs text-gray-400">{{ report.totals.isProjected ? 'projected from 90 days' : (report.totals.isComplete ? 'measured' : 'not enough history') }}</div>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="text-xs font-semibold text-gray-500 uppercase mb-1">12-month contribution</div>
        <div class="text-2xl font-bold text-gray-900">{{ report.totals.twelveMonthContributionLtv != null ? (report.totals.twelveMonthContributionLtv | currency:'INR':'symbol':'1.0-0') : '—' }}</div>
        <div class="text-xs text-gray-400">{{ report.variableCostPerPickup != null ? 'after ' + (report.variableCostPerPickup | currency:'INR':'symbol':'1.0-0') + ' per pickup' : 'no cost per pickup configured' }}</div>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="text-xs font-semibold text-gray-500 uppercase mb-1">90-day LTV</div>
        <div class="text-2xl font-bold text-gray-900">{{ checkpoint(report.totals, 90)?.ltv != null ? (checkpoint(report.totals, 90)!.ltv | currency:'INR':'symbol':'1.0-0') : '—' }}</div>
        <div class="text-xs text-gray-400">{{ checkpoint(report.totals, 90)?.matureCustomers | number }} customers with 90+ days</div>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Pickup growth 90d → 1 year</div>
        <div class="text-2xl font-bold text-gray-900">{{ report.projectionRatio != null ? '×' + report.projectionRatio : '—' }}</div>
        <div class="text-xs text-gray-400">pickups, from {{ report.projectionBasisCustomers | number }} customers with a first pickup 1–2 years ago</div>
      </div>
    </div>

    <div *ngIf="report.cohorts.length === 0" class="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500 text-sm">
      No customer had their first pickup in this period.
    </div>

    <ng-container *ngIf="report.cohorts.length > 0">
      <h3 class="text-sm font-semibold text-gray-700 mb-2">By quarter (first pickup)</h3>
      <div class="bg-white rounded-lg border border-gray-200 mb-6 overflow-x-auto">
        <table class="min-w-full text-sm">
          <ng-container *ngTemplateOutlet="header; context: { $implicit: 'Quarter' }"></ng-container>
          <tbody>
            <ng-container *ngFor="let row of report.cohorts">
              <ng-container *ngTemplateOutlet="metricRow; context: { $implicit: row, label: quarterLabel(row.quarter), total: false }"></ng-container>
            </ng-container>
            <ng-container *ngTemplateOutlet="metricRow; context: { $implicit: report.totals, label: 'Total', total: true }"></ng-container>
          </tbody>
        </table>
      </div>

      <h3 class="text-sm font-semibold text-gray-700 mb-2">By acquisition channel</h3>
      <div class="bg-white rounded-lg border border-gray-200 mb-4 overflow-x-auto">
        <table class="min-w-full text-sm">
          <ng-container *ngTemplateOutlet="header; context: { $implicit: 'Channel' }"></ng-container>
          <tbody>
            <ng-container *ngFor="let row of report.channels">
              <ng-container *ngTemplateOutlet="metricRow; context: { $implicit: row, label: row.label, total: false }"></ng-container>
            </ng-container>
          </tbody>
        </table>
      </div>
    </ng-container>

    <p class="text-xs text-gray-400 leading-relaxed">
      LTV is ClearScrap revenue (platform charges + voucher commission, not GMV) per customer within 30, 90, 180 and 365 days of their
      first valid pickup, up to {{ report.asOf | date:'d MMM y' }}. Each column only counts customers whose first pickup was at least that
      many days ago, so the number under each value is how many customers it's based on. When a group hasn't had a full year, the 12-month
      figure is its 90-day value × how much the number of pickups grew from 90 days to a year for customers whose first pickup was one to
      two years ago (marked "projected"). Pickups rather than revenue, because those customers started when pickups were free, so their
      revenue grew with the platform fee rather than with repeat business; each projected pickup keeps today's revenue per pickup.
      <ng-container *ngIf="report.variableCostPerPickup == null">Contribution needs a variable cost per pickup in the API settings
        (MarketingAttributionSettings:LtvVariableCostPerPickup).</ng-container>
      Compare 12-month LTV with CAC per first pickup on the Funnel tab: 3× or more is healthy.
    </p>
  </div>
</div>

<ng-template #header let-first>
  <thead class="bg-gray-50 text-xs text-gray-500 uppercase">
    <tr>
      <th class="text-left px-3 py-2">{{ first }}</th>
      <th class="text-right px-3 py-2" title="People whose first pickup was in this group's dates, one per phone number">Customers</th>
      <th *ngFor="let days of checkpointDays" class="text-right px-3 py-2" [title]="'Revenue per customer within ' + days + ' days of their first pickup'">{{ days }}-day LTV</th>
      <th class="text-right px-3 py-2">12-month LTV</th>
      <th class="text-right px-3 py-2" title="12-month LTV minus the variable cost of each pickup">12-month contribution</th>
    </tr>
  </thead>
</ng-template>

<ng-template #metricRow let-m let-label="label" let-total="total">
  <tr class="border-t" [class.border-gray-100]="!total" [class.border-t-2]="total" [class.border-gray-200]="total"
    [class.bg-gray-50]="total" [class.font-semibold]="total">
    <td class="px-3 py-2 text-gray-900 whitespace-nowrap">{{ label }}</td>
    <td class="px-3 py-2 text-right">{{ m.customers | number }}</td>
    <td *ngFor="let days of checkpointDays" class="px-3 py-2 text-right whitespace-nowrap">
      <ng-container *ngIf="checkpoint(m, days)?.ltv != null; else none">
        {{ checkpoint(m, days)!.ltv | currency:'INR':'symbol':'1.0-0' }}
        <span class="text-xs text-gray-400" [title]="'Customers with ' + days + '+ days since their first pickup'">({{ checkpoint(m, days)!.matureCustomers | number }})</span>
      </ng-container>
    </td>
    <td class="px-3 py-2 text-right text-green-700 whitespace-nowrap">
      <ng-container *ngIf="m.twelveMonthLtv != null; else none">
        {{ m.twelveMonthLtv | currency:'INR':'symbol':'1.0-0' }}
        <span *ngIf="m.isProjected" class="text-xs text-gray-400">projected</span>
      </ng-container>
    </td>
    <td class="px-3 py-2 text-right whitespace-nowrap">
      <ng-container *ngIf="m.twelveMonthContributionLtv != null; else none">{{ m.twelveMonthContributionLtv | currency:'INR':'symbol':'1.0-0' }}</ng-container>
    </td>
  </tr>
</ng-template>

<ng-template #none><span class="text-gray-400">—</span></ng-template>
